import { Button, Divider, Tooltip, Typography } from "@suraasa/placebo-ui"
import { useMutation, useQuery } from "@tanstack/react-query"
import api from "api"
import { SavedAddress } from "api/resources/payment/types"
import { Product } from "api/resources/products/types"
import RazorpayLogo from "assets/razorpayLogo.webp"
import StripeLogo from "assets/stripeLogo.webp"
import AsyncBuilder from "components/AsyncBuilder"
import ErrorPage from "components/ErrorPage"
import { AuthManager } from "features/Authentication/AuthManager"
import { useGlobalContext } from "global/Context/context"
import { useEffect, useRef, useState } from "react"
import { Link, useSearchParams } from "react-router-dom"
import { getPlatformURL } from "utils/helpers"
import useCurrentCountry from "utils/hooks/useCurrentCountry"
import useFormatPrice from "utils/hooks/useFormatPrice"

import { usePgctlPayment } from "../usePgctlPayment"

type Price = {
  currency: {
    code: string
    symbol: string
  }
  itemPrice: number
  finalAmount: number
  discountAmount: number

  taxes: { amount: number; name: string }[]
}

interface OrderSummaryProps {
  onPaymentSuccess: (orderId: string) => void
  address: SavedAddress | null
  autoTriggerPayment?: boolean
}

const getPurchaseDetails = (
  product: Product | undefined,
  price: Price | undefined | null
) => {
  if (!product || !price) return null

  const details = {
    itemName: product.name,
    amount: price.finalAmount,
    currency: {
      code: price.currency.code,
      symbol: price.currency.symbol,
    },
  }

  return details
}

const SESSION_KEY = "pgctl_payment_auto_triggered"

const OrderSummary = ({
  onPaymentSuccess: onPayNow,
  address,
  autoTriggerPayment,
}: OrderSummaryProps) => {
  const [searchParams] = useSearchParams()
  const formatPrice = useFormatPrice()
  const currentCountry = useCurrentCountry()

  // Don't use this state directly. Use priceDisplay instead
  const [_priceWithTax, _setPriceWithTax] = useState<Price | null>()
  const [productAvailable, setProductAvailable] = useState(true)

  const orderIdRef = useRef<string | null>(null)

  const { isAuthenticated } = useGlobalContext()

  const productSlug = searchParams.get("product")

  const authInfo = AuthManager.getPlatformAuth()

  // Gives prices without tax (auto-detects country based on IP)
  const product = useQuery({
    queryFn: () =>
      api.products.getProduct({
        urlParams: { productSlug: productSlug || "" },
        params: {
          fields: ["registration_fee", "prices"],
        },
      }),
    queryKey: ["product", productSlug],
  })

  const getOrderId = () => {
    return orderIdRef.current
  }

  const payment = usePgctlPayment({
    purchaseDetails: getPurchaseDetails(product.data, _priceWithTax),
    onSuccessfulCompletion: () => {
      const orderId = getOrderId()
      if (!orderId) {
        throw Error("Order id was not found")
      }
      onPayNow(orderId)
    },
  })

  const getPricesWithTax = useMutation({
    mutationFn: () =>
      api.products.getProductTax({
        data: {
          product_slug: productSlug,
          country_code: address?.country?.isoCode,
          state_code: address?.state?.isoCode,
        },
      }),
    onSuccess: data => {
      if (!data.registrationFee) {
        setProductAvailable(false)
      } else {
        setProductAvailable(true)
        _setPriceWithTax({
          currency: data.registrationFee.currency,
          itemPrice: data.registrationFee.originalAmount,
          finalAmount: data.registrationFee.finalAmount,
          discountAmount: 0,
          taxes: data.registrationFee.taxes,
        })
      }
    },
  })

  useEffect(() => {
    if (authInfo?.user.email && address && productSlug) {
      getPricesWithTax.mutate()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [address, productSlug, authInfo?.user.email])

  const priceDisplay = (() => {
    // priceWithTax
    if (_priceWithTax) {
      if (_priceWithTax.finalAmount == null) {
        return "Unavailable"
      }
      return (
        _priceWithTax.currency.symbol + formatPrice(_priceWithTax.finalAmount)
      )
    }

    // priceWithoutTax (Initial display when country is not selected)
    if (product.data) {
      if (
        product.data.prices == null ||
        product.data.prices.registrationFee == null
      ) {
        return "Unavailable"
      }
      return (
        product.data.prices.registrationFee.currency.symbol +
        formatPrice(product.data.prices.registrationFee.price)
      )
    }

    return "Not available in your region"
  })()

  const enablePayNow = isAuthenticated && address

  const executePayment = async () => {
    if (!address?.uuid || !productSlug) {
      throw new Error("billingAddressId or product slug cannot be null")
    }
    const res = await payment.executePaymentFlow(address.uuid, productSlug)
    if (res) {
      orderIdRef.current = res.id
    }
  }

  useEffect(() => {
    if (
      autoTriggerPayment &&
      !sessionStorage.getItem(SESSION_KEY) &&
      enablePayNow &&
      _priceWithTax
    ) {
      sessionStorage.setItem(SESSION_KEY, "true")
      executePayment()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoTriggerPayment, enablePayNow, _priceWithTax])

  const isIndia = address?.country.name
    ? address?.country.name === "India"
    : currentCountry?.name === "India"

  return (
    <>
      {payment.stripePaymentElement}
      {productAvailable ? (
        <div className="mb-10 h-full px-2 pt-3 sm:pt-9 md:mx-auto md:max-w-md">
          <Typography variant="title4" className="mb-4">
            Order Summary
          </Typography>
          <div className="mb-2 flex flex-row justify-between">
            <Typography variant={"body"} className="text-onSurface-600">
              Item
            </Typography>
            {product.isSuccess && (
              <Typography variant={"strong"} className="text-onSurface-600">
                {priceDisplay}
              </Typography>
            )}
          </div>

          <div className="mb-2 flex flex-col gap-2">
            {_priceWithTax &&
              _priceWithTax.taxes.map(tax => (
                <div key={tax.name} className="flex flex-row justify-between">
                  <Typography variant={"body"} className="text-onSurface-600">
                    {tax.name}
                  </Typography>
                  <Typography variant={"strong"} className="text-onSurface-600">
                    {_priceWithTax.currency.symbol}
                    {formatPrice(tax.amount)}
                  </Typography>
                </div>
              ))}
          </div>

          <Divider weight="light" className="mb-2" />
          {product.isSuccess && (
            <div className="mb-5 flex flex-row justify-between">
              <Typography variant="strong">Total</Typography>
              <Typography variant={"title2"}>{priceDisplay}</Typography>
            </div>
          )}

          <Tooltip
            enabled={!enablePayNow}
            content={"Please complete your billing details first"}
          >
            <AsyncBuilder
              handler={executePayment}
              render={({ onClick, loading }) => (
                <Button
                  color="black"
                  label="Pay Now"
                  className="mb-2 w-full"
                  onClick={onClick}
                  disabled={
                    !enablePayNow || !address || product.isError || !productSlug
                  }
                  loading={loading}
                />
              )}
            />
          </Tooltip>

          {isIndia ? (
            <div className="mb-6 flex flex-row items-center justify-center gap-0.5">
              <Typography variant={"smallBody"} className="text-onSurface-500">
                Secured by
              </Typography>
              <img src={RazorpayLogo} alt="Razorpay" className="h-2" />
            </div>
          ) : (
            <div className="mb-6 flex flex-row items-center justify-center gap-0.5">
              <Typography variant={"smallBody"} className="text-onSurface-500">
                Powered by
              </Typography>
              <img src={StripeLogo} alt="stripe" className="h-2" />
            </div>
          )}
          <Typography
            variant={"smallBody"}
            className="text-center text-secondary-500"
          >
            This fee is non-refundable. By proceeding you agree to accept our
            <Link
              to={getPlatformURL("suraasa", "/terms-of-use")}
              target="_blank"
            >
              <span className="text-interactive-500"> terms & conditions</span>
            </Link>
          </Typography>
        </div>
      ) : (
        <ErrorPage
          className="mt-10 min-h-[auto]"
          errorStatus={404}
          title="Product not available"
          description="This product is not available in your region."
        />
      )}
    </>
  )
}

export default OrderSummary
